import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { StorageFunctions } from '../../../general/storage';
import { ConfiguracaoItensNfeForm, ConfiguracaoItensNfeViewModel } from './model-configuracao-itens-nfe/ConfiguracaoItensNfeViewModel';
import { UnidadesEmpresaViewModel } from 'models/unidades-empresa/UnidadesEmpresaViewModel';
import { CodigosBeneficiosFiscaisService } from '../../../services/codigos-beneficios-fiscais.service';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-configuracao-itens-nfe',
  templateUrl: './configuracao-itens-nfe.component.html',
  styleUrls: ['./configuracao-itens-nfe.component.scss']
})
export class ConfiguracaoItensNfeComponent implements OnInit {
  private storageKey = 'ConfiguracaoItensNfeComponent';

  @Output() opened = new EventEmitter();
  @Output() closed = new EventEmitter();
  @Output() applied = new EventEmitter<ConfiguracaoItensNfeViewModel>();

  @Input() open = false;
  @Input() tipoNfeID: number = null;
  @Input() cst: string = null;
  @Input() cBenefDefault: () => string = null;
  @Input() cstDefault: () => string = null;
  @Input() unidadeEmpresa: UnidadesEmpresaViewModel = null;

  clienteSimples: boolean = false;
  cstsListCRTNormal = [
    { id: '0', nome: '00 - Tributada Integralmente' },
    { id: '10', nome: '10 - Tributada e com Cobrança do ICMS por Substituição Tributária' },
    { id: '20', nome: '20 - Com redução da Base de Cálculo' },
    { id: '40', nome: '40 - Isenta' },
    { id: '41', nome: '41 - Não Tributada' },
    { id: '51', nome: '51 - Diferimento' },
  ];

  cstsListCRTSimples = [
    { id: '101', nome: '101 - Tributada com Permissão de Credito' },
    { id: '400', nome: '400 - Não tributada' },
    { id: '900', nome: '900 - Outros' },
  ];

  loading = [];
  codigosCreditoPresumido = [];

  form = this.getFormDefault();

  constructor(
    private storage: StorageFunctions,
    private codigosBeneficiosFiscaisService: CodigosBeneficiosFiscaisService,
  ) { }    

  get crtSimples() {
    return this.unidadeEmpresa && [1, 2].includes(this.unidadeEmpresa.crt);
  }

  get crtNormal() {
    return this.unidadeEmpresa && [3, 4].includes(this.unidadeEmpresa.crt);
  }

  ngOnInit(): void {
    this.carregaCodigoCreditoPresumido();
    this.setCstDefault();

    this.form.get('aplicarCst').valueChanges.subscribe((b: boolean) => {
      if (b) {
        this.form.get('cst').enable();
        this.setCstDefault();
      } else {
        this.form.get('cst').disable();
        this.form.get('cst').setValue(null);
        this.setDataToStorage();
      }
    });

    this.form.get('aplicarCCredPresumido').valueChanges.subscribe((b: boolean) => {
      if (b) {
        this.form.get('cCredPresumido').enable();
      } else {
        this.form.get('cCredPresumido').disable();
        this.form.get('cCredPresumido').setValue(null);
        this.setDataToStorage();
      }
    });
  }

  getFormDefault(): FormGroup<ConfiguracaoItensNfeForm> {
    const f = new FormGroup({
      replicarParaNovosItens: new FormControl(false),
      aplicarCbenef: new FormControl(false),
      cBenef: new FormControl(null),
      aplicarCst: new FormControl(false),
      cst: new FormControl({ value: null, disabled: true }),
      aplicarCCredPresumido: new FormControl(false),
      cCredPresumido: new FormControl({ value: null, disabled: true }),
    });

    return f;
  }

  onOpened() {
    this.setDataFromStorage();
    this.opened.emit();
  }

  onClosed() {
    this.closed.emit();
  }

  onSubmit() {
    this.setDataToStorage();
    this.applied.emit(this.form.getRawValue());
  }

  onCancel() {
    this.closed.emit();
  }

  private setCstDefault() {
    if (!this.cstDefault || typeof this.cstDefault !== 'function') {
      return;
    }

    this.cst = this.cstDefault();
    if (this.cst) {
      this.form.get('cst').setValue(this.cst);
      this.setDataToStorage();
    }
  }

  private setCBenefDefault() {
    if (!this.cBenefDefault || typeof this.cBenefDefault !== 'function') {
      return;
    }

    const cBenef = this.cBenefDefault();
    if (cBenef) {
      this.form.get('cBenef').setValue(cBenef);
      this.setDataToStorage();
    }
  }

  private setDataFromStorage() {
    const storageMapData = this.storage.getDataFromLocalStorage(this.storageKey) || {};
    if (this.tipoNfeID === null || !(this.tipoNfeID in storageMapData)) {
      this.setCBenefDefault();
    } else {
      this.form.patchValue(storageMapData[this.tipoNfeID]);
      this.form.get('replicarParaNovosItens').setValue(false);
    }
  }

  private setDataToStorage() {
    const storageMapData = this.storage.getDataFromLocalStorage(this.storageKey) || {};
    storageMapData[this.tipoNfeID] = this.form.getRawValue();
    this.storage.saveDataToLocalStorage(this.storageKey, storageMapData);
  }

  async carregaCodigoCreditoPresumido() {
    try {
      this.loading['codigosCreditoPresumido'] = true;
      this.codigosCreditoPresumido = await firstValueFrom(this.codigosBeneficiosFiscaisService.getCreditoPresumido());
    } catch (error) {
      console.error(error);
    } finally {
      this.loading['codigosCreditoPresumido'] = false;
    }
  }
}
